import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

// Icons
import { SlArrowLeft } from "react-icons/sl";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { CiCalendar, CiSearch } from "react-icons/ci";
import { IoMdCheckmark } from "react-icons/io";

// Components
import DashboardLayout from "../DashboardHome/DashboardLayout";
import Loader from "../Loader";

// Assets
import TimeIcon from "../../assets/images/timeDate.svg";
import LocationIcon from "../../assets/images/Locationx.svg";

// Actions
import {
  fetchOnePublicEvents,
  updateEventByUser,
} from "../../redux/posts/posts.action";
import { Avatar } from "@chakra-ui/react";

// Custom Hooks
const useEventInfo = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { publicEvent, updateEventsLoading, publicEventLoading } = useSelector(
    (state) => state.posts
  );

  const { accessToken: token } = useSelector((state) => state.auth);
  const { darktheme } = useSelector((state) => state.features.settings);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/home");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (id) {
      dispatch(fetchOnePublicEvents(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (id && !updateEventsLoading) {
      dispatch(fetchOnePublicEvents(id));
    }
  }, [updateEventsLoading, id, dispatch]);

  return {
    publicEvent,
    publicEventLoading,
    darktheme,
    id,
    dispatch,
    navigate,
  };
};

// Components
const SearchBar = ({ darktheme }) => (
  <div
    className={`flex items-center w-[540px] mx-auto h-[36px] gap-[10px] ${
      darktheme ? "bg-[black] border-[#fafafa]" : "bg-[#F2F2F5]"
    } rounded-[31px] mb-[28px] border-[1px] border-[#02023414] py-[8px] px-[12px]`}
  >
    <CiSearch className="text-[#60646C] w-[16px] h-[16px]" />
    <input
      placeholder="Search for Events"
      className="w-full h-full bg-transparent text-[#828282] text-[14px] border-none outline-none"
    />
  </div>
);

const BackButton = ({ navigate, darktheme }) => (
  <div className="flex mb-[1.5rem]">
    <button
      onClick={() => navigate(-1)}
      className={`bg-transparent ${
        darktheme ? "text-[#f2f2f5]" : "text-[#525866]"
      }  rounded-[50%] border-[#E2E4E9] border-[1px] w-[36px] h-[36px] p-[.8rem] flex items-center justify-center hover:bg-gray-50 transition-colors`}
      aria-label="Go back"
    >
      <SlArrowLeft />
    </button>
  </div>
);

const EventImage = ({ images, title }) => (
  <div className="w-[540px] bg-black h-[273px] rounded-[8px] overflow-hidden">
    {images && images.length > 0 && (
      <img
        src={images[0]}
        alt={`${title} event cover`}
        className="w-full object-cover h-full rounded-[8px]"
        loading="lazy"
      />
    )}
  </div>
);

const EventDetails = ({ event, darktheme }) => {
  // Get event date and time
  const getEventDateTime = () => {
    if (!event?.date || !event?.time) return null;
    return `${event.date} – ${event.time}`;
  };

  return (
    <div className="my-[8px]">
      <h1
        className={`${
          darktheme ? "text-[#f2f2f5]" : "text-[#525866]"
        }  font-[600] text-lg mb-2`}
      >
        {event?.title}
      </h1>

      {/* Date and Time */}
      {getEventDateTime() && (
        <div
          className={`${
            darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"
          }  items-center flex text-[12px] font-[400] mb-[4px]`}
        >
          <img
            src={TimeIcon}
            alt="Time"
            className={`${darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"}`}
          />
          <p className="ml-[4px]">{getEventDateTime()}</p>
        </div>
      )}

      {/* Location */}
      {event?.address && (
        <div
          className={`${
            darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"
          }  items-center flex text-[12px] font-[400] mb-[4px]`}
        >
          <img
            src={LocationIcon}
            alt="Location"
            className={`${darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"}`}
          />
          <p
            className={`ml-[4px] ${
              darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"
            } `}
          >
            {event.address}
          </p>
        </div>
      )}
    </div>
  );
};

const InterestButton = ({
  event,
  onUpdate,
  isOpen,
  onToggleDropdown,
  darktheme,
}) => {
  if (event?.interested) {
    return (
      <div className="relative  flex gap-[4px] w-full justify-between items-center">
        <button className="rounded-[999px] text-base text-[#525866] border-[1px] border-[#e2e4e9] text-[14px] font-[600] w-[490px] gap-[4px] flex items-center justify-center p-[10px] h-[40px] bg-transparent">
          <IoMdCheckmark className="text-[#525866]" />
          Interested
        </button>

        <button
          onClick={onToggleDropdown}
          className="cursor-pointer border-[1px] border-[#e2e4e9] rounded-[50%] w-[36px] h-[36px] flex items-center justify-center hover:bg-gray-50 transition-colors"
          aria-label="Event options"
        >
          <HiOutlineDotsHorizontal className="text-[#525866]" />
        </button>

        {isOpen && (
          <div
            className={`absolute -bottom-[4.8rem] ${darktheme ? "border-white":"border-[#1a1a1a21]"} shadow-lg border-[1px]  rounded-[5px] cursor-pointer w-[150px] -right-[120px] px-[4px] py-[4px] pb-[.5rem] mx-auto text-[.8rem] z-10 ${
              darktheme ? "bg-black text-white" : "bg-[#fff] text-[#303030]"
            }`}
          >
            <button
              onClick={onUpdate}
              className="w-full text-left mb-[.rem] text-[13px] font-[450] hover:rounded-[5px] hover:bg-[#F2F4F7] hover:text-[#525866] py-[8px] px-[.5rem] transition-colors"
            >
              Not interested
            </button>
            <button className="w-full text-left mb-[.rem] text-[13px] font-[450] hover:rounded-[5px] hover:bg-[#F2F4F7] hover:text-[#525866] pb-[8px] px-[.5rem] transition-colors">
              Going
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={onUpdate}
      className="rounded-[999px] text-white w-full gap-[4px] flex items-center justify-center p-[10px] h-[40px] button-black-gradient hover:opacity-90 transition-opacity"
    >
      <CiCalendar />
      Interested?
    </button>
  );
};

const EventStats = ({ event, darktheme }) => (
  <div
    className={`flex items-center my-[8px] gap-[4px] text-[12px] ${
      darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"
    }`}
  >
    <p>{event?.interestedAttendeesCount || 0} Interested</p>
    <div className="w-[4px] h-[4px] bg-[#b9bbc6] rounded-[50%]" />
    <p>0 Going</p>
  </div>
);

const EventOrganizer = ({ event, darktheme }) => {
  // Get organizer display name
  const getOrganizerName = () => {
    return event?.user?.displayName || event?.user?.username || "Anonymous";
  };

  // Get formatted creation time
  const getCreatedTime = () => {
    return event?.createdAt ? moment(event.createdAt).fromNow() : "";
  };

  return (
    <div className="border-y-[1px] flex justify-between items-center my-[8px] py-[8px] border-y-[#02023414]">
      <div>
        <p
          className={`text-[12px] font-[600] ${
            darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"
          } mb-2`}
        >
          Organizer
        </p>
        <div className="flex gap-[4px] mt-[8px]">
          {/* Organizer Avatar */}
            {event?.user?.imageUrl && (
               <Avatar
                              name={getOrganizerName()}
                              src={event.user.imageUrl}
                              width={"44px"}
                              height={"44px"}
                              className="mr-2"
                            />
            )}

          {/* Organizer Info */}
          <div>
            <h4
              className={`text-[14px] font-[510px] ${
                darktheme ? "text-[#f2f2f5]" : "text-[#1c2024]"
              }`}
            >
              {getOrganizerName()}
            </h4>
            <p
              className={`text-[12px] ${
                darktheme ? "text-[#f2f2f5]" : "text-[#60646c]"
              }`}
            >
              {getCreatedTime()}
            </p>
          </div>
        </div>
      </div>

      <button
        className="cursor-pointer p-1 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Organizer options"
      >
        <HiOutlineDotsHorizontal
          className={`w-[24px] h-[24px] ${
            darktheme ? "text-[#f2f2f5]" : "text-[#1c2024]"
          }`}
        />
      </button>
    </div>
  );
};

const EventDescription = ({ event, darktheme }) => (
  <div>
    <h2
      className={`my-[8px] font-[700] text-[16px] ${
        darktheme ? "text-[#f2f2f5]" : "text-[#1c2024]"
      }`}
    >
      About event
    </h2>
    <p
      className={`font-[400] text-[14px] my-[8px] ${
        darktheme ? "text-[#f2f2f5]" : "text-[#1c2024]"
      } leading-relaxed`}
    >
      {event?.description}
    </p>
  </div>
);

function EventInfo() {
  const { publicEvent, publicEventLoading, darktheme, id, dispatch, navigate } =
    useEventInfo();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Handlers
  const handleToggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleUpdateEvent = () => {
    dispatch(updateEventByUser(id));
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  };

  // Loading state
  if (publicEventLoading) {
    return (
      <DashboardLayout>
        <div className="mx-auto flex min-h-screen justify-center bg-white items-center">
          <Loader big={true} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div
        className={`h-auto pb-[8rem] min-h-screen pt-[8px] ${
          darktheme ? "bg-[black]" : "bg-white"
        } w-full`}
      >
        {/* Search Bar */}
        <SearchBar darktheme={darktheme} />

        {/* Main Content */}
        <div className="max-w-[540px] mx-auto pt-[1rem]">
          {/* Back Button */}
          <BackButton navigate={navigate} darktheme={darktheme} />

          {/* Event Image */}
          <EventImage
            images={publicEvent?.pictures}
            title={publicEvent?.title}
          />

          {/* Event Details */}
          <EventDetails event={publicEvent} darktheme={darktheme} />

          {/* Interest Section */}
          <div className="relative">
            <InterestButton
              event={publicEvent}
              onUpdate={handleUpdateEvent}
              isOpen={isDropdownOpen}
              onToggleDropdown={handleToggleDropdown}
              darktheme={darktheme}
            />

            {/* Event Stats */}
            <EventStats event={publicEvent} darktheme={darktheme} />
          </div>

          {/* Organizer Section */}
          <EventOrganizer event={publicEvent} darktheme={darktheme} />

          {/* Event Description */}
          <EventDescription event={publicEvent} darktheme={darktheme} />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default EventInfo;
